import { useRef, useEffect, type ReactNode } from 'react';
import { Palette, LogOut } from 'lucide-react';
import { useCEAuthStore } from '../auth/store';
import { Button } from '@components/base/actions';
import { Text, Icon } from '@components/base/display';
import { HStack, VStack } from '@components/base/layout';
import { ThemeOverride } from '@components/integrated/theme-override';
import { defaultDarkTheme } from '@/theme/darkTheme';
import { ThemeToggle } from './shared/ThemeToggle';

interface ComponentExplorerLayoutProps {
  sidebar: ReactNode;
  children: ReactNode;
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
  componentId?: string;
}

export function ComponentExplorerLayout({
  sidebar,
  children,
  isDarkMode,
  onToggleDarkMode,
  componentId,
}: Readonly<ComponentExplorerLayoutProps>) {
  const mainRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    /* v8 ignore start */
    if (mainRef.current) {
      /* v8 ignore stop */
      mainRef.current.scrollTop = 0;
    }
  }, [componentId]);

  const logout = useCEAuthStore((state) => state.logout);

  const content = (
    <HStack gap={0} className="h-screen overflow-hidden">
      {/* Sidebar */}
      <VStack
        gap={0}
        className="w-72 flex-shrink-0 border-r border-secondary-200 bg-secondary-50"
      >
        <HStack
          gap={3}
          align="center"
          className="border-b border-secondary-200 px-4 py-4"
        >
          <Icon icon={Palette} size="lg" color="primary" />
          <Text as="h1" size="lg" weight="bold" color="primary">
            Component Explorer
          </Text>
        </HStack>
        <VStack gap={0} className="flex-1 overflow-y-auto">
          {sidebar}
        </VStack>
      </VStack>

      {/* Main content */}
      <VStack
        gap={0}
        className="flex-1 min-w-0 bg-secondary-100"
      >
        <HStack
          as="header"
          justify="end"
          align="center"
          gap={2}
          className="border-b border-secondary-200 px-6 py-3"
        >
          <ThemeToggle isDarkMode={isDarkMode} onToggle={onToggleDarkMode} />
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Icon icon={LogOut} size="sm" />}
            onClick={() => void logout()}
          >
            Logout
          </Button>
        </HStack>
        <VStack as="main" ref={mainRef} gap={0} className="flex-1 overflow-y-auto p-6">
          {children}
        </VStack>
      </VStack>
    </HStack>
  );

  if (isDarkMode) {
    return (
      <ThemeOverride config={defaultDarkTheme} className="h-screen">
        {content}
      </ThemeOverride>
    );
  }

  return content;
}
